import type { MaybeAsync } from "@mdotm/utils/promise";
import { Dialog, DialogFooter, type DialogProps } from "@mdotm/shadcn/legacy/components";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { type RwAtom, readAtom, useAtomState, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type MaybeFn, maybeCall } from "@mdotm/shadcn/legacy/utils";
import type { ReactNode } from "react";

export type SpawnBulkActionReviewDialogParams<T> = {
	header: string;
	children: MaybeFn<ReactNode, [RwAtom<T>]>;
	canConfirm: MaybeFn<boolean, [T]>;
	initialState: T;
	onContinue?: MaybeAsync<void, [T]>;
};

export function BulkActionReviewDialog<T>({
	children,
	header,
	show,
	canConfirm,
	onContinue,
	onCancel,
	onAnimationStateChange,
	initialState,
}: Omit<SpawnBulkActionReviewDialogParams<T>, "onContinue"> & {
	onContinue: MaybeAsync<void, [T]>;
	onCancel(): void;
} & Pick<DialogProps, "show" | "onAnimationStateChange">): React.JSX.Element {
	const [state$] = useAtomState(initialState);
	return (
		<Dialog
			size="large"
			show={show}
			onClose={onCancel}
			onAnimationStateChange={onAnimationStateChange}
			header={header}
			footer={
				<DialogFooter
					primaryAction={
						<WithAtom atom$={state$}>
							{(state) => (
								<Button
									disabled={!maybeCall(canConfirm, state)}
									palette="primary"
									size="small"
									data-qualifier="BulkActionReviewDialog/ConfirmButton"
									onClickAsync={() => onContinue(readAtom(state$))}
								>
									Confirm
								</Button>
							)}
						</WithAtom>
					}
					neutralAction={
						<Button
							palette="tertiary"
							size="small"
							type="button"
							onClick={onCancel}
							data-qualifier="BulkActionReviewDialog/CancelButton"
						>
							Cancel
						</Button>
					}
				/>
			}
		>
			{maybeCall(children, state$)}
		</Dialog>
	);
}

type SpawnBulkActionReviewDialogResult<T> = { kind: "continue"; result: T } | { kind: "cancel" };

/** Note: never throws */
export function spawnBulkActionReviewDialog<T>({
	onContinue,
	...forward
}: SpawnBulkActionReviewDialogParams<T>): Promise<SpawnBulkActionReviewDialogResult<T>> {
	return spawn<SpawnBulkActionReviewDialogResult<T>>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<BulkActionReviewDialog
				{...forward}
				show={show}
				onContinue={async (result) => {
					await onContinue?.(result);
					resolve({ kind: "continue", result });
				}}
				onCancel={() => resolve({ kind: "cancel" })}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
			/>
		)),
	).promise;
}
